/**
 * CommentFilterPopover —— 任务列表「评论筛选」入口。
 *
 * 现状把「评论标题 / 评论关键词 / 有无评论」三个筛选框合并成 1 个类 Select
 * 的触发器 + 弹层,空间省 2/3;外观贴齐 antd Select 风格(灰边框 + placeholder),
 * 与筛选栏其它下拉视觉一致。
 *
 * 设计详见 docs/mockups/comment-filter-ui.html 方案 A。
 *
 * 契约:
 * - 值受控:父组件持有 `value: CommentFilterValue`,onChange 提交时回调
 * - 「应用」按钮点击 → onChange(v);「清空」→ onChange(EMPTY);「取消」→ 不回调
 * - Popover 关闭时若草稿态与 value 不同,自动丢弃(避免"我以为改了")
 */
import React, { useEffect, useState } from 'react';
import { Button, Input, Popover } from 'antd';
import { DownOutlined, CloseCircleFilled } from '@ant-design/icons';

export type CommentScope = 'both' | 'title' | 'body';

export interface CommentFilterValue {
  /** 关键词,空串/undefined = 不搜 */
  keyword?: string;
  /** 关键词匹配范围;仅在 keyword 非空时有意义 */
  scope: CommentScope;
  /** true=只看有评论;false=只看无评论;undefined=不过滤 */
  hasComment?: boolean;
}

export const EMPTY_COMMENT_FILTER: CommentFilterValue = { scope: 'both' };

/** 该值是否表示"有筛选"(会展示激活态 + 红点)。 */
export const isCommentFilterActive = (v: CommentFilterValue): boolean =>
  !!(v.keyword && v.keyword.trim()) || v.hasComment !== undefined;

interface Props {
  value: CommentFilterValue;
  onChange: (v: CommentFilterValue) => void;
  /** 触发器宽度,默认 200px 与筛选栏其它 Select 对齐 */
  width?: number;
  /** 未激活时的 placeholder,类似"请选择评论筛选" */
  placeholder?: string;
}

/** 分段控件:与 antd Segmented 类似,但更小,风格贴齐 popover 布局。 */
const Seg: React.FC<{
  options: { label: string; value: string }[];
  value: string;
  onChange: (v: string) => void;
}> = ({ options, value, onChange }) => (
  <div style={{
    display: 'inline-flex',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    overflow: 'hidden',
    fontSize: 12.5,
  }}>
    {options.map((o, i) => {
      const on = o.value === value;
      return (
        <div
          key={o.value}
          onClick={() => onChange(o.value)}
          style={{
            padding: '4px 12px',
            cursor: 'pointer',
            background: on ? '#5e6ad2' : '#fff',
            color: on ? '#fff' : '#6b6f76',
            borderLeft: i === 0 ? 'none' : '1px solid #e5e7eb',
            userSelect: 'none',
            transition: 'background .12s',
          }}
        >
          {o.label}
        </div>
      );
    })}
  </div>
);

const CommentFilterPopover: React.FC<Props> = ({
  value, onChange,
  width = 200,
  placeholder = '请选择评论筛选',
}) => {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);
  // 草稿态:打开面板时快照,应用/取消决定是否覆盖到父。
  // 目的:用户改到一半点空白关掉,不算作提交,不产生歧义。
  const [draft, setDraft] = useState<CommentFilterValue>(value);

  useEffect(() => {
    // 面板每次打开都以最新 value 为草稿起点
    if (open) setDraft(value);
  }, [open, value]);

  const active = isCommentFilterActive(value);

  const summary = (): string => {
    const parts: string[] = [];
    if (value.keyword && value.keyword.trim()) {
      const scopeLabel = value.scope === 'title' ? '标题'
        : value.scope === 'body' ? '正文' : '内容';
      parts.push(`${scopeLabel}含"${value.keyword.trim()}"`);
    }
    if (value.hasComment === true) parts.push('有评论');
    if (value.hasComment === false) parts.push('无评论');
    return parts.join(' · ');
  };

  const applyDraft = () => {
    // 关键词为空时,scope 无意义但保留(下次打开还能记忆);
    // 保持 draft 原样落回。清空 keyword.trim 后统一为 undefined,避免空串走 SQL。
    const normalized: CommentFilterValue = {
      keyword: draft.keyword?.trim() || undefined,
      scope: draft.scope,
      hasComment: draft.hasComment,
    };
    onChange(normalized);
    setOpen(false);
  };

  const clearAll = () => {
    onChange(EMPTY_COMMENT_FILTER);
    setDraft(EMPTY_COMMENT_FILTER);
    setOpen(false);
  };

  const cancel = () => setOpen(false);

  const content = (
    <div style={{ width: 320, padding: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 44, fontSize: 12.5, color: '#6b6f76', textAlign: 'right' }}>范围</div>
        <Seg
          options={[
            { label: '两者', value: 'both' },
            { label: '标题', value: 'title' },
            { label: '正文', value: 'body' },
          ]}
          value={draft.scope}
          onChange={(v) => setDraft({ ...draft, scope: v as CommentScope })}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 44, fontSize: 12.5, color: '#6b6f76', textAlign: 'right' }}>关键词</div>
        <Input
          placeholder={
            draft.scope === 'title' ? '按评论标题模糊搜' :
            draft.scope === 'body' ? '按评论正文模糊搜' :
            '搜标题或正文'
          }
          value={draft.keyword ?? ''}
          onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
          onPressEnter={applyDraft}
          allowClear
          size="small"
          style={{ flex: 1 }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 44, fontSize: 12.5, color: '#6b6f76', textAlign: 'right' }}>存在</div>
        <Seg
          options={[
            { label: '全部', value: '__any' },
            { label: '有评论', value: '__yes' },
            { label: '无评论', value: '__no' },
          ]}
          value={
            draft.hasComment === true ? '__yes' :
            draft.hasComment === false ? '__no' : '__any'
          }
          onChange={(v) => setDraft({
            ...draft,
            hasComment: v === '__yes' ? true : v === '__no' ? false : undefined,
          })}
        />
      </div>
      <div style={{
        display: 'flex', justifyContent: 'flex-end', gap: 8,
        marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0',
      }}>
        <Button type="link" size="small" onClick={clearAll}>清空</Button>
        <Button size="small" onClick={cancel}>取消</Button>
        <Button type="primary" size="small" onClick={applyDraft}>应用</Button>
      </div>
    </div>
  );

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      open={open}
      onOpenChange={setOpen}
      content={content}
      // 面板挂到 body 避免被工具栏 overflow 裁切
      getPopupContainer={() => document.body}
    >
      {/* Select-like 触发器:视觉与 antd Select 一致,灰边框 + placeholder,不用 icon 前缀。
          激活时正文变实色;悬停/展开时边框变蓝。右侧一键清除小 x 与 antd Select 一致。 */}
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width,
          height: 28,
          padding: '0 8px 0 11px',
          border: `1px solid ${open || hover ? '#5e6ad2' : '#d9d9d9'}`,
          borderRadius: 6,
          background: '#fff',
          cursor: 'pointer',
          fontSize: 13,
          color: active ? 'rgba(0, 0, 0, 0.88)' : '#bfbfbf',
          transition: 'border-color .15s',
          userSelect: 'none',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {active ? summary() : placeholder}
        </span>
        {active && hover ? (
          <CloseCircleFilled
            onClick={(e) => {
              e.stopPropagation();
              onChange(EMPTY_COMMENT_FILTER);
              setDraft(EMPTY_COMMENT_FILTER);
            }}
            style={{ color: 'rgba(0,0,0,0.25)', fontSize: 12, marginLeft: 6 }}
          />
        ) : (
          <DownOutlined
            style={{
              color: '#bfbfbf',
              fontSize: 10,
              marginLeft: 6,
              transition: 'transform .15s',
              transform: open ? 'rotate(180deg)' : 'rotate(0)',
            }}
          />
        )}
      </div>
    </Popover>
  );
};

export default CommentFilterPopover;
